import React, { useState, useRef, useMemo } from 'react';
import { Reply, Smile, Edit2, Trash2, Bot, ListTree, Languages, MessageSquareReply, StickyNote, CheckSquare } from 'lucide-react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import EditMessageForm from './EditMessageForm';
import ReactionBar from './ReactionBar';
import TeamChatFilePreview from './TeamChatFilePreview';
import EmojiPicker from './EmojiPicker';

export default function TeamMessageItem({
    message,
    onReply,
    onEdit,
    onDelete,
    onToggleReaction,
    onScrollToMessage,
    isConversationAdmin,
    onAiAction,
    onSaveToNotes,
    onCreateTask,
    onViewNote
}) {
    const { theme } = useTheme();
    const { currentUser } = useAuth();
    const [showActions, setShowActions] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const emojiTriggerRef = useRef(null);

    const isBot = message.user?.is_bot === true;
    const isOwnMessage = !isBot && message.user?.id === currentUser?.id;
    const canEdit = isOwnMessage && new Date() - new Date(message.created_at) < 15 * 60 * 1000;
    const canDelete = isOwnMessage || isConversationAdmin;

    // Render markdown for bot messages
    const formattedContent = useMemo(() => {
        if (!isBot || !message.content) return null;
        try {
            const renderer = new marked.Renderer();
            const originalLink = renderer.link;
            renderer.link = function(href, title, text) {
                const html = originalLink.call(this, href, title, text);
                return html.replace(/^<a /, '<a target="_blank" rel="noopener noreferrer" ');
            };
            const rawHTML = marked(message.content, { gfm: true, breaks: true, renderer });
            return DOMPurify.sanitize(rawHTML, {
                ADD_ATTR: ['target', 'rel'],
                ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                    'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a',
                    'table', 'thead', 'tbody', 'tr', 'td', 'th'],
                ALLOWED_ATTR: ['href', 'target', 'rel', 'class']
            });
        } catch {
            return null;
        }
    }, [isBot, message.content]);

    // Render message content with @mentions highlighted
    const renderContentWithMentions = (text) => {
        if (!text) return null;
        const parts = text.split(/(@\w+)/g);
        return parts.map((part, i) => {
            if (/^@\w+$/.test(part)) {
                const mentionedUsername = part.slice(1);
                const isSelf = mentionedUsername.toLowerCase() === currentUser?.username?.toLowerCase();
                return (
                    <span
                        key={i}
                        className={`inline-block px-1 rounded font-semibold ${
                            isSelf
                                ? 'bg-cyan-500 text-white'
                                : isOwnMessage
                                    ? 'bg-white/20 text-white'
                                    : theme === 'dark'
                                        ? 'bg-blue-500/20 text-blue-400'
                                        : 'bg-blue-100 text-blue-700'
                        }`}
                    >
                        {part}
                    </span>
                );
            }
            return part;
        });
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const handleEditSave = (content) => {
        onEdit(message.id, content);
        setIsEditing(false);
    };

    if (message.message_type === 'system') {
        return (
            <div className={`text-center py-2 text-xs font-rasa ${
                theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
            }`}>
                {message.content}
            </div>
        );
    }

    return (
        <div
            id={`msg-${message.id}`}
            className={`group flex px-4 py-1.5 relative ${
                isOwnMessage ? 'justify-end' : 'justify-start'
            }`}
            onMouseEnter={() => setShowActions(true)}
            onMouseLeave={() => {
                if (!showEmojiPicker) {
                    setShowActions(false);
                }
            }}
        >
            <div className={`flex gap-2.5 max-w-[75%] ${
                isOwnMessage ? 'flex-row-reverse' : 'flex-row'
            }`}>
                {/* Avatar */}
                <div className="flex-shrink-0 pt-0.5">
                    {isBot ? (
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white bg-gradient-to-br from-purple-500 to-indigo-600">
                            <Bot size={16} />
                        </div>
                    ) : message.user?.avatar_url ? (
                        <img src={message.user.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-unna ${
                            isOwnMessage ? 'bg-blue-500' : 'bg-purple-500'
                        }`}>
                            {(message.user?.first_name || message.user?.username || '?').charAt(0).toUpperCase()}
                        </div>
                    )}
                </div>

                {/* Bubble */}
                <div className={`relative min-w-0 max-w-full ${isOwnMessage ? 'items-end' : 'items-start'}`}>
                    {/* Sender name + time */}
                    <div className={`flex items-baseline gap-2 mb-0.5 ${
                        isOwnMessage ? 'justify-end' : 'justify-start'
                    }`}>
                        <span className={`text-sm font-unna font-semibold ${isBot ? 'text-purple-400' : ''}`}>
                            {isOwnMessage
                                ? 'You'
                                : message.user?.first_name
                                    ? `${message.user.first_name} ${message.user.last_name || ''}`
                                    : message.user?.username}
                        </span>
                        {isBot && (
                            <span className="text-[10px] font-rasa px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-400 border border-purple-500/30">
                                Bot
                            </span>
                        )}
                        <span className={`text-xs font-rasa ${
                            theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                            {formatTime(message.created_at)}
                        </span>
                    </div>

                    {/* Reply quote */}
                    {message.reply_to && (
                        <button
                            onClick={() => onScrollToMessage?.(message.reply_to.id)}
                            className={`flex items-center gap-2 mb-1 px-2.5 py-1 rounded-lg text-xs font-rasa border-l-2 w-full ${
                                theme === 'dark'
                                    ? 'bg-gray-800 border-blue-400 text-gray-400 hover:bg-gray-700'
                                    : 'bg-gray-100 border-blue-500 text-gray-500 hover:bg-gray-200'
                            }`}
                        >
                            <span className="font-unna font-medium">{message.reply_to.user?.username}</span>
                            <span className="truncate">{message.reply_to.content}</span>
                        </button>
                    )}

                    {/* Message bubble */}
                    <div className={`rounded-2xl px-3.5 py-2 overflow-hidden break-words ${
                        isBot
                            ? theme === 'dark'
                                ? 'bg-purple-900/40 border border-purple-700/50 text-gray-100 rounded-tl-sm'
                                : 'bg-purple-50 border border-purple-200 text-gray-900 rounded-tl-sm'
                            : isOwnMessage
                                ? 'bg-blue-500 text-white rounded-tr-sm'
                                : theme === 'dark'
                                    ? 'bg-gray-700 text-gray-100 rounded-tl-sm'
                                    : 'bg-gray-200 text-gray-900 rounded-tl-sm'
                    }`}>
                        {isEditing ? (
                            <EditMessageForm
                                message={message}
                                onSave={handleEditSave}
                                onCancel={() => setIsEditing(false)}
                            />
                        ) : message.message_type === 'note' && message.note ? (
                            /* Shared note card */
                            <div
                                onClick={() => onViewNote?.(message.note?.id || message.content)}
                                role="button"
                                tabIndex={0}
                                className={`block rounded-lg p-3 no-underline transition-colors ${
                                    theme === 'dark' ? 'bg-gray-600/50 hover:bg-gray-600' : 'bg-white/80 hover:bg-white'
                                }`}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <StickyNote size={14} className="text-blue-500 flex-shrink-0" />
                                    <span className={`text-sm font-unna font-semibold truncate ${
                                        theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                                    }`}>
                                        {message.note.title}
                                    </span>
                                </div>
                                {message.note.content_preview && (
                                    <p className={`text-xs font-rasa line-clamp-2 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                                    }`}>
                                        {message.note.content_preview}
                                    </p>
                                )}
                                <div className="flex items-center gap-2 mt-1.5">
                                    {message.note.category && message.note.category.split(',').slice(0, 2).map(c => c.trim()).filter(Boolean).map(cat => (
                                        <span key={cat} className={`text-[9px] font-rasa px-1.5 py-0.5 rounded ${
                                            theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-500'
                                        }`}>{cat}</span>
                                    ))}
                                    <span className={`text-[9px] font-rasa ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                                        by {message.note.owner?.first_name || message.note.owner?.username}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <>
                                {!(message.content === '(file)' && message.attachments?.length > 0) && (
                                    isBot && formattedContent ? (
                                        <div className="text-sm font-rasa break-words overflow-x-auto">
                                            <div
                                                dangerouslySetInnerHTML={{ __html: formattedContent }}
                                                className="prose prose-sm dark:prose-invert max-w-none
                                                    [&_table]:block [&_table]:overflow-x-auto [&_table]:w-max [&_table]:min-w-full
                                                    prose-table:border-collapse
                                                    prose-th:border prose-th:border-purple-300 dark:prose-th:border-purple-700
                                                    prose-th:px-3 prose-th:py-1.5 prose-th:text-left prose-th:whitespace-nowrap prose-th:bg-purple-100 dark:prose-th:bg-purple-900/50
                                                    prose-td:border prose-td:border-purple-200 dark:prose-td:border-purple-800
                                                    prose-td:px-3 prose-td:py-1.5
                                                    prose-p:my-2 prose-ul:my-2 prose-ol:my-2
                                                    prose-headings:mt-3 prose-headings:mb-1"
                                            />
                                            {message.edited && (
                                                <span className={`text-xs ${
                                                    theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                                }`}>(edited)</span>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-sm font-rasa whitespace-pre-wrap break-words">
                                            {renderContentWithMentions(message.content)}
                                            {message.edited && (
                                                <span className={`text-xs ml-1 ${
                                                    isOwnMessage ? 'text-blue-200' : theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                                }`}>(edited)</span>
                                            )}
                                        </p>
                                    )
                                )}

                                <TeamChatFilePreview attachments={message.attachments} />
                            </>
                        )}
                    </div>

                    <ReactionBar
                        reactions={message.reactions}
                        onToggleReaction={(emoji) => onToggleReaction(message.id, emoji)}
                    />

                    {/* Hover actions — positioned on the bubble */}
                    {showActions && !isEditing && (
                        <div className={`absolute -top-3 ${isOwnMessage ? 'right-0' : 'left-0'} flex items-center gap-0.5 px-1 py-0.5 rounded-lg border shadow-sm z-10 ${
                            theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                        }`}>
                            <button
                                onClick={() => onReply(message)}
                                className={`p-1.5 rounded ${
                                    theme === 'dark' ? 'hover:bg-gray-600' : 'hover:bg-gray-100'
                                }`}
                                title="Reply"
                            >
                                <Reply size={14} />
                            </button>
                            <div className="relative">
                                <button
                                    ref={emojiTriggerRef}
                                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                    className={`p-1.5 rounded ${
                                        theme === 'dark' ? 'hover:bg-gray-600' : 'hover:bg-gray-100'
                                    }`}
                                    title="React"
                                >
                                    <Smile size={14} />
                                </button>
                                {showEmojiPicker && (
                                    <EmojiPicker
                                        triggerRef={emojiTriggerRef}
                                        onSelect={(emoji) => onToggleReaction(message.id, emoji)}
                                        onClose={() => {
                                            setShowEmojiPicker(false);
                                            setShowActions(false);
                                        }}
                                    />
                                )}
                            </div>
                            {canEdit && (
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className={`p-1.5 rounded ${
                                        theme === 'dark' ? 'hover:bg-gray-600' : 'hover:bg-gray-100'
                                    }`}
                                    title="Edit"
                                >
                                    <Edit2 size={14} />
                                </button>
                            )}
                            {canDelete && (
                                <button
                                    onClick={() => onDelete(message.id)}
                                    className={`p-1.5 rounded text-red-400 ${
                                        theme === 'dark' ? 'hover:bg-gray-600' : 'hover:bg-gray-100'
                                    }`}
                                    title="Delete"
                                >
                                    <Trash2 size={14} />
                                </button>
                            )}

                            {/* Save to Notes button */}
                            {onSaveToNotes && (
                                <button
                                    onClick={() => onSaveToNotes(message.content)}
                                    className={`p-1.5 rounded ${
                                        theme === 'dark' ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                                    }`}
                                    title="Save to Notes"
                                >
                                    <StickyNote size={14} />
                                </button>
                            )}

                            {/* Add to Tasks button */}
                            {onCreateTask && (
                                <button
                                    onClick={() => onCreateTask(message.content)}
                                    className={`p-1.5 rounded ${
                                        theme === 'dark' ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                                    }`}
                                    title="Add to Tasks"
                                >
                                    <CheckSquare size={14} />
                                </button>
                            )}

                            {/* AI action buttons */}
                            {onAiAction && (
                                <>
                                    <div className={`w-px h-4 mx-0.5 ${
                                        theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'
                                    }`} />
                                    <button
                                        onClick={() => onAiAction('summarize')}
                                        className={`p-1.5 rounded text-purple-400 ${
                                            theme === 'dark' ? 'hover:bg-gray-600' : 'hover:bg-gray-100'
                                        }`}
                                        title="Summarize conversation"
                                    >
                                        <ListTree size={14} />
                                    </button>
                                    <button
                                        onClick={() => onAiAction('translate', message.id, message.content)}
                                        className={`p-1.5 rounded text-purple-400 ${
                                            theme === 'dark' ? 'hover:bg-gray-600' : 'hover:bg-gray-100'
                                        }`}
                                        title="Translate"
                                    >
                                        <Languages size={14} />
                                    </button>
                                    <button
                                        onClick={() => onAiAction('suggest_reply', message.id, message.content)}
                                        className={`p-1.5 rounded text-purple-400 ${
                                            theme === 'dark' ? 'hover:bg-gray-600' : 'hover:bg-gray-100'
                                        }`}
                                        title="Suggest reply"
                                    >
                                        <MessageSquareReply size={14} />
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
